/** Собственный профиль: имя, пароль, активность. */

import type { ProfileDto } from '@project-os/contracts';
import type { ChangePasswordInput, UpdateProfileInput } from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiUpload } from './client';
import { sessionQueryKey } from './auth';

export const profileKey = ['auth', 'profile'] as const;

export function useProfile() {
  return useQuery({
    queryKey: profileKey,
    queryFn: ({ signal }) => apiRequest<ProfileDto>('/auth/profile', { signal }),
  });
}

/** Имя видно во всех проектах, поэтому обновляется и сессия. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) =>
      apiRequest<ProfileDto>('/auth/profile', { method: 'PATCH', body: input }),
    onSuccess: (profile) => {
      queryClient.setQueryData(profileKey, profile);
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      apiRequest<{ closedSessions: number }>('/auth/profile/password', {
        method: 'POST',
        body: input,
      }),
    // Число действующих сеансов после смены пароля меняется.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKey }),
  });
}

/**
 * Загрузка своего аватара.
 *
 * После загрузки сбрасывается и профиль, и сессия: аватар виден в шапке,
 * а её рисует сессия.
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => apiUpload<{ avatarUrl: string }>('/auth/profile/avatar', file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKey });
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useClearAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/auth/profile/avatar', { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKey });
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}
